// src/encounter/services/use-save-social-history.ts
// Rail-scoped save hook for the IPS Social History section. Wraps the curl-verified social-history
// endpoints (POST /api/Patients/CreateSocialHistory, PUT /api/Patients/socialHistory/{id}) and
// invalidates BOTH the encounter summary query (['patient-summary', patientId] — the rail render
// source) AND the chart-detail list (['patient-social-history', patientId] — the full-record
// source edit/delete look records up from), so a freshly-added/edited row's full record can't go
// stale and block the affordance round-trip. Mirrors use-save-condition / use-save-allergy:
// transport + cache only; body assembly + wire-shape rules live in lib/to-social-history-request.ts.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, put } from '@/shared/api/api-client';
import type { SocialHistoryRequest } from '@/encounter/lib/to-social-history-request';

export function useSaveSocialHistory(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, SocialHistoryRequest>({
    mutationFn: (req: SocialHistoryRequest) => {
      if (req.kind === 'create') {
        return post<string>('/api/Patients/CreateSocialHistory', req.body);
      }
      // update path covers EDIT and soft-DELETE.
      return put<string>(`/api/Patients/socialHistory/${req.socialHistoryId}`, req.body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
      qc.invalidateQueries({ queryKey: ['patient-social-history', patientId] });
    },
  });
}
